import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  TextField,
  Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import {
  useBulkUploadItem,
  useGetBulkUploadStatus,
  useDownloadBulkUploadTemplate,
} from '../../Query/StoreItemQuery';
import { BulkUploadItemAddedByEnum, GetAllStoresForAdminLimitEnum, GetAllStoresForAdminSortByEnum, GetAllStoresForAdminSortOrderEnum, GetAllStoresForAdminStatusFilterEnum, GetAllStoresForAdminAddedByFilterEnum } from '../../../client-sdk/api';
import { useSnackbar } from '../../Stateless/SnackBarProvider';
import { useAllStores } from '../../Query/ManageStoreQuery';
import { useStoreManagementParams } from '../StoreManagement/useStoreManagementParams';
import { useContext } from 'react';
import { HimmaContext } from '../../../context/HimmaContext';
import * as XLSX from 'xlsx';


interface BulkUploadModalProps {
  open: boolean;
  onClose: () => void;
  storeId?: string; // Made optional since we'll have internal selection
  merchantId?: string;
  addedBy: BulkUploadItemAddedByEnum;
  onSuccess?: () => void;
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  open,
  onClose,
  storeId: propStoreId,
  merchantId,
  addedBy,
  onSuccess,
}) => {
  const { showMessage } = useSnackbar();
  const api = useContext(HimmaContext)!;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileRowCount, setFileRowCount] = useState<number | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [selectedStoreId, setSelectedStoreId] = useState<string>(propStoreId || '');
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const bulkUploadMutation = useBulkUploadItem();
  const { data: statusData } = useGetBulkUploadStatus(jobId);
  const downloadTemplateMutation = useDownloadBulkUploadTemplate();

  // Use store management params like other components
  const { params: storeParams } = useStoreManagementParams();
  const isReady = Boolean(storeParams && Object.keys(storeParams).length > 0);
  
  const memoizedParams = useMemo(() => {
    // Always provide default params if storeParams is not ready
    const baseParams: any = {
      page: 1,
      limit: 100, // Maximum allowed by API
      sortBy: 'createdAt',
      sortOrder: 'desc',
      statusFilter: 'All',
      addedByFilter: 'All',
    };

    if (!isReady) {
      return baseParams;
    }
    
    const params: any = {
      ...storeParams,
      limit: 100, // Maximum allowed by API (10, 25, 50, or 100)
    };
    
    // Remove merchantId from params to fetch all stores
    delete params.merchantId;
    
    return params;
  }, [isReady, storeParams]);

  // State to hold all fetched stores
  const [allStores, setAllStores] = useState<any[]>([]);
  const [isLoadingAllStores, setIsLoadingAllStores] = useState(false);

  // Fetch stores for dropdown - only when modal is open (first page for initial load)
  const fallbackParams: any = {
    page: 1,
    limit: 100,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    statusFilter: 'All',
    addedByFilter: 'All',
  };
  
  const { data: storesData, isLoading: isStoresLoading, refetch: refetchStores } = useAllStores(
    open ? memoizedParams : fallbackParams
  );

  // Fetch all stores by making multiple API calls if needed
  useEffect(() => {
    if (!open) {
      setAllStores([]);
      return;
    }

    const fetchAllStores = async () => {
      setIsLoadingAllStores(true);
      try {
        // First, get the first page to know total count
        // Don't pass merchantId to fetch all stores
        const firstResponse = await api.getAllStoresForAdmin(
          1,
          100 as GetAllStoresForAdminLimitEnum,
          'createdAt' as GetAllStoresForAdminSortByEnum,
          'desc' as GetAllStoresForAdminSortOrderEnum,
          'All' as GetAllStoresForAdminStatusFilterEnum,
          'All' as GetAllStoresForAdminAddedByFilterEnum,
          undefined, // merchantId - not passed to get all stores
          undefined, // storeType
          undefined  // search
        );
        
        const totalItems = firstResponse.data?.pagination?.totalItems || 0;
        const firstPageStores = firstResponse.data?.data || [];
        
        // If total items <= 100, we already have all stores
        if (totalItems <= 100) {
          setAllStores(firstPageStores);
          setIsLoadingAllStores(false);
          return;
        }
        
        // Calculate how many more pages we need
        const totalPages = Math.ceil(totalItems / 100);
        const allStoresList = [...firstPageStores];
        
        // Fetch remaining pages
        for (let page = 2; page <= totalPages; page++) {
          const pageResponse = await api.getAllStoresForAdmin(
            page,
            100 as GetAllStoresForAdminLimitEnum,
            'createdAt' as GetAllStoresForAdminSortByEnum,
            'desc' as GetAllStoresForAdminSortOrderEnum,
            'All' as GetAllStoresForAdminStatusFilterEnum,
            'All' as GetAllStoresForAdminAddedByFilterEnum,
            undefined, // merchantId - not passed to get all stores
            undefined, // storeType
            undefined  // search
          );
          const pageStores = pageResponse.data?.data || [];
          allStoresList.push(...pageStores);
        }
        
        setAllStores(allStoresList);
      } catch (error) {
        console.error('Error fetching all stores:', error);
        // Fallback to first page data if available
        if (storesData?.data) {
          setAllStores(storesData.data);
        }
      } finally {
        setIsLoadingAllStores(false);
      }
    };

    fetchAllStores();
  }, [open, api]);

  // Prepare store options from all fetched stores
  const storeOptions = useMemo(() => {
    const storesToUse = allStores.length > 0 ? allStores : (storesData?.data || []);
    if (!Array.isArray(storesToUse) || storesToUse.length === 0) {
      return [];
    }
    return storesToUse.map((store: any) => ({
      value: store.id,
      label: store.restaurantDetails?.storeName || store.nameEnglish || store.name || 'Unknown Store',
    }));
  }, [allStores, storesData]);

  // Update loading state to include fetching all stores
  const isStoresLoadingFinal = isStoresLoading || isLoadingAllStores;

  // Reset and refresh when modal opens/closes
  useEffect(() => {
    if (open) {
      // Reset all state when modal opens
      setSelectedFile(null);
      setFileRowCount(null);
      setJobId(null);
      setSelectedStoreId(propStoreId || '');
      setIsDragActive(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Refetch stores when modal opens
      refetchStores();
    }
  }, [open, propStoreId, refetchStores]);

  // Count rows in the file
  const countFileRows = async (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          // Subtract 1 for header row
          resolve(Math.max(0, jsonData.length));
        } catch (error) {
          // If it's a CSV file, try parsing as CSV
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const data = new Uint8Array(arrayBuffer);
          const text = new TextDecoder().decode(data);
          const lines = text.split('\n').filter((line) => line.trim() !== '');
          // Subtract 1 for header row
          resolve(Math.max(0, lines.length - 1));
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const validateAndProcessFile = async (file: File) => {
    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv', // .csv
    ];
    const validExtensions = ['.xlsx', '.xls', '.csv'];

    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isValidType = validTypes.includes(file.type) || validExtensions.includes(fileExtension);

    if (!isValidType) {
      showMessage('Please select a valid Excel (.xlsx, .xls) or CSV (.csv) file', 'error');
      return;
    }

    setSelectedFile(file);
    setJobId(null);

    // Count rows in the file
    try {
      const rowCount = await countFileRows(file);
      setFileRowCount(rowCount);
    } catch (error) {
      console.error('Error counting file rows:', error);
      setFileRowCount(null);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await validateAndProcessFile(file);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (isUploading || isPolling || !selectedStoreId) {
      return;
    }

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    await validateAndProcessFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showMessage('Please select a file', 'error');
      return;
    }
    if (!selectedStoreId) {
      showMessage('Please select a store first', 'error');
      return;
    }

    try {
      const response = await bulkUploadMutation.mutateAsync({
        storeId: selectedStoreId,
        addedBy,
        file: selectedFile,
        merchantId,
      });

      // Check if response has jobId (background job) or is immediate success
      if (response?.jobId) {
        // Background job (>50 records)
        setJobId(response.jobId);
        showMessage(
          `Upload started in background. Processing ${response.estimatedRows || fileRowCount || 0} records...`,
          'info'
        );
      } else {
        // Immediate success (<=50 records)
        showMessage(
          response?.message || `Successfully uploaded ${response?.itemsCreated || 0} items`,
          'success'
        );
        handleClose();
        if (onSuccess) onSuccess();
      }
    } catch (error: any) {
      // Error handling is done in the mutation
      console.error('Upload error:', error);
    }
  };

  // Poll status when jobId is set
  useEffect(() => {
    if (jobId && statusData) {
      if (statusData.status === 'completed') {
        showMessage(
          statusData.result?.message ||
            `Successfully uploaded ${statusData.result?.itemsCreated || 0} items`,
          'success'
        );
        handleClose();
        if (onSuccess) onSuccess();
      } else if (statusData.status === 'failed') {
        showMessage('Bulk upload failed. Please check the errors and try again.', 'error');
      }
    }
  }, [jobId, statusData]);

  const handleClose = () => {
    setSelectedFile(null);
    setFileRowCount(null);
    setJobId(null);
    setSelectedStoreId(propStoreId || '');
    setIsDragActive(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const handleDownloadTemplate = () => {
    if (!selectedStoreId) {
      showMessage('Please select a store first', 'error');
      return;
    }
    downloadTemplateMutation.mutate(selectedStoreId);
  };

  const isUploading = bulkUploadMutation.isPending;
  const isPolling = jobId && statusData && (statusData.status === 'queued' || statusData.status === 'processing');
  const isCompleted = statusData?.status === 'completed';
  const isFailed = statusData?.status === 'failed';

  const getStatusColor = () => {
    if (isCompleted) return 'success';
    if (isFailed) return 'error';
    if (isPolling) return 'info';
    return 'default';
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Bulk Upload Items</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          {/* Store Selection Section */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
              Select Store <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Autocomplete
              options={storeOptions}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={storeOptions.find((opt) => opt.value === selectedStoreId) || null}
              onChange={(_, newValue) => {
                setSelectedStoreId(newValue ? newValue.value : '');
              }}
              loading={isStoresLoadingFinal}
              disabled={isUploading || isPolling}
              fullWidth
              filterOptions={(options, { inputValue }) => {
                // Custom filter for case-insensitive search
                return options.filter((option) =>
                  option.label.toLowerCase().includes(inputValue.toLowerCase())
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search and select a store"
                  variant="outlined"
                  fullWidth
                  error={!selectedStoreId}
                  helperText={
                    !selectedStoreId
                      ? 'Please select a store before uploading items'
                      : ''
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                    },
                    '& .MuiInputBase-input': {
                      padding: '10px 14px',
                    },
                  }}
                />
              )}
              noOptionsText={
                isStoresLoadingFinal ? 'Loading stores...' : 'No stores found'
              }
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.value}>
                  {option.label}
                </Box>
              )}
            />
          </Box>

          {/* Template Download Section */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Download Template
            </Typography>
            <Stack direction="row" spacing={2} mt={1}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
                disabled={downloadTemplateMutation.isPending || !selectedStoreId}
                size="small"
              >
                Download Excel
              </Button>
            </Stack>
          </Box>

          {/* File Upload Section */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Select File
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="bulk-upload-file-input"
            />
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onClick={() => {
                if (!isUploading && !isPolling && selectedStoreId) {
                  fileInputRef.current?.click();
                }
              }}
              sx={{
                border: isDragActive ? '2px dashed #1976d2' : '2px dashed #ccc',
                borderRadius: 2,
                p: 3,
                mt: 1,
                background: isDragActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                transition: 'border 0.2s, background 0.2s',
                textAlign: 'center',
                cursor: isUploading || isPolling || !selectedStoreId ? 'not-allowed' : 'pointer',
                opacity: isUploading || isPolling || !selectedStoreId ? 0.6 : 1,
                minHeight: 120,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <UploadFileIcon
                sx={{
                  fontSize: 48,
                  color: isDragActive ? '#1976d2' : 'text.secondary',
                  mb: 2,
                }}
              />
              <Typography
                variant="body1"
                color={isDragActive ? 'primary' : 'text.secondary'}
                sx={{ mb: 1, fontWeight: isDragActive ? 600 : 400 }}
              >
                {isDragActive
                  ? 'Drop file here...'
                  : selectedFile
                  ? selectedFile.name
                  : 'Drag & drop file here or click to browse'}
              </Typography>
              {!selectedFile && (
                <Typography variant="caption" color="text.secondary">
                  Supported formats: .xlsx, .xls, .csv
                </Typography>
              )}
              {!selectedStoreId && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  Please select a store first
                </Typography>
              )}
            </Box>
            {fileRowCount !== null && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {fileRowCount} records found
                {fileRowCount > 50 && ' (will be processed in background)'}
              </Typography>
            )}
          </Box>

          {/* Upload Progress */}
          {isUploading && (
            <Box>
              <Typography variant="body2" gutterBottom>
                Uploading file...
              </Typography>
              <LinearProgress />
            </Box>
          )}

          {/* Background Job Status */}
          {jobId && statusData && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2">Job Status:</Typography>
                <Chip
                  label={statusData.status?.toUpperCase() || 'UNKNOWN'}
                  color={getStatusColor() as any}
                  size="small"
                />
              </Box>
              {statusData.progress !== undefined && (
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Progress</Typography>
                    <Typography variant="body2">{statusData.progress}%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={statusData.progress} />
                </Box>
              )}
              {statusData.totalRows && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Processed: {statusData.processedRows || 0} / {statusData.totalRows}
                  {statusData.successfulRows !== undefined && (
                    <> | Successful: {statusData.successfulRows}</>
                  )}
                  {statusData.failedRows !== undefined && statusData.failedRows > 0 && (
                    <> | Failed: {statusData.failedRows}</>
                  )}
                </Typography>
              )}
            </Box>
          )}

          {/* Errors */}
          {statusData?.errors && statusData.errors.length > 0 && (
            <Alert severity="error">
              <Typography variant="subtitle2" gutterBottom>
                Validation Errors:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                {statusData.errors.slice(0, 10).map((error: { row: number; message: string; field?: string }, index: number) => (
                  <li key={index}>
                    <Typography variant="caption">
                      Row {error.row}: {error.message}
                      {error.field && ` (Field: ${error.field})`}
                    </Typography>
                  </li>
                ))}
                {statusData.errors.length > 10 && (
                  <Typography variant="caption" color="text.secondary">
                    ... and {statusData.errors.length - 10} more errors
                  </Typography>
                )}
              </Box>
            </Alert>
          )}

          {/* Error from mutation */}
          {bulkUploadMutation.isError && !jobId && (
            <Alert severity="error">
              <Typography variant="subtitle2" gutterBottom>
                {bulkUploadMutation.error?.response?.data?.message || 'Validation Errors:'}
              </Typography>
              {bulkUploadMutation.error?.response?.data?.errors && 
               Array.isArray(bulkUploadMutation.error.response.data.errors) &&
               bulkUploadMutation.error.response.data.errors.length > 0 && (
                <Box component="ul" sx={{ pl: 2, mb: 0, mt: 1 }}>
                  {bulkUploadMutation.error.response.data.errors.slice(0, 10).map((error: { row?: number; message: string; field?: string }, index: number) => (
                    <li key={index}>
                      <Typography variant="caption">
                        {error.row !== undefined ? `Row ${error.row + 1}: ` : ''}
                        {error.message}
                        {error.field && ` (Field: ${error.field})`}
                      </Typography>
                    </li>
                  ))}
                  {bulkUploadMutation.error.response.data.errors.length > 10 && (
                    <Typography variant="caption" color="text.secondary">
                      ... and {bulkUploadMutation.error.response.data.errors.length - 10} more errors
                    </Typography>
                  )}
                </Box>
              )}
              {(!bulkUploadMutation.error?.response?.data?.errors || 
                !Array.isArray(bulkUploadMutation.error.response.data.errors) ||
                bulkUploadMutation.error.response.data.errors.length === 0) && (
                <Typography variant="body2">
                  {bulkUploadMutation.error?.response?.data?.message || 'Failed to upload file. Please try again.'}
                </Typography>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isPolling}>
          {isCompleted || isFailed ? 'Close' : 'Cancel'}
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!selectedFile || !selectedStoreId || isUploading || isPolling || isCompleted}
          startIcon={isUploading ? <CircularProgress size={16} /> : null}
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadModal;

